import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ManageBus = () => {
  // Get sessionId from localStorage
  const sessionId = localStorage.getItem("currentSessionId"); // session ID from localStorage
  const [busDetails, setBusDetails] = useState([]);
  const [form, setForm] = useState({
    busNumber: '',
    driver: '',
    driverMobNumber: '',
    conductor: '',
    conductorMobNumber: '',
    studentCapacity: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentBusId, setCurrentBusId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false); // Loading state for fetching data

  // Fetch bus details based on session ID
  useEffect(() => {
    if (!sessionId) {
      setMessage('Please select a session first.');
      return;
    }
    fetchBusDetails();
  }, [sessionId]);

  // Fetch Bus Details from API
  const fetchBusDetails = async () => {
    setLoading(true);  // Set loading to true when starting to fetch
    try { 
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/busdetails/getAllBusDetails`, {
        params: { sessionId }
      });
      setBusDetails(response.data);
      setMessage('');  // Clear any previous error messages
    } catch (error) {
      console.error('Error fetching bus details:', error);
      setMessage('Failed to fetch bus details.');
    } finally {
      setLoading(false);  // Set loading to false once fetching is done
    }
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) return setMessage('Session not selected');

    setLoading(true);  // Set loading to true when starting submission
    try {
      if (isEditing) {
        if (window.confirm('Are you sure you want to update this bus detail?')) {
          await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/busdetails/updateBusDetails/${currentBusId}`, form, {
            params: { sessionId },
            headers: {  Authorization: `Bearer ${localStorage.getItem("token")}` }
          });
          alert('Bus detail updated successfully');
        }
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/busDetails/createBusDetail`, form, {
          params: { sessionId },
          headers: {  Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        alert('Bus detail created successfully');
      }
      fetchBusDetails(); // Refresh the list of bus details
      setForm({
        busNumber: '',
        driver: '',
        driverMobNumber: '',
        conductor: '',
        conductorMobNumber: '',
        studentCapacity: '',
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving bus detail:', error);
      setMessage(`Failed to save bus detail: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);  // Set loading to false once submission is done
    }
  };

  const handleEdit = (busDetail) => {
    setForm(busDetail);
    setCurrentBusId(busDetail._id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!sessionId) return setMessage('Session not selected');

    if (window.confirm('Are you sure you want to delete this bus detail?')) {
      setLoading(true);  // Set loading to true when starting delete operation
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/busdetails/deleteBusDetails/${id}`, {
          params: { sessionId },
          headers: {  Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        alert('Bus detail deleted successfully');
        fetchBusDetails();
      } catch (error) {
        console.error('Error deleting bus detail:', error);
        setMessage('Failed to delete bus detail.');
      } finally {
        setLoading(false);  // Set loading to false once delete operation is done
      }
    }
  };

  return (
    <div className="container mx-auto p-4">
      {/* Show message for errors */}
      {message && <p className="text-red-500 mb-4">{message}</p>}

      {/* Bus Detail Form */}
      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 shadow rounded-md">
        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            name="busNumber"
            placeholder="Bus Number"
            value={form.busNumber}
            onChange={handleInputChange}
            className="border p-2 rounded"
            required
          />
          <input
            type="text"
            name="driver"
            placeholder="Driver"
            value={form.driver}
            onChange={handleInputChange}
            className="border p-2 rounded"
            required
          />
          <input
            type="number"
            name="driverMobNumber"
            placeholder="Driver Mobile Number"
            value={form.driverMobNumber}
            onChange={handleInputChange}
            className="border p-2 rounded"
            required
          />
          <input
            type="text"
            name="conductor"
            placeholder="Conductor (Optional)"
            value={form.conductor}
            onChange={handleInputChange}
            className="border p-2 rounded"
          />
          <input
            type="number"
            name="conductorMobNumber"
            placeholder="Conductor Mobile Number"
            value={form.conductorMobNumber}
            onChange={handleInputChange}
            className="border p-2 rounded"
          />
          <input
            type="number"
            name="studentCapacity"
            placeholder="Student Capacity"
            value={form.studentCapacity}
            onChange={handleInputChange}
            className="border p-2 rounded"
            required
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-md text-white font-semibold bg-blue-600 hover:bg-blue-700 transition"
        >
          {isEditing ? 'Update Bus Detail' : 'Add Bus Detail'}
        </button>
      </form>

      {/* Bus Details Table */}
      <h2 className="text-xl font-semibold mt-8 mb-4">All Bus Details</h2>
      {loading ? (  // Show loading spinner while fetching or performing an operation
        <div className="text-center">
          <span className="text-blue-500">Loading...</span>
        </div>
      ) : (
        <table className="min-w-full bg-white shadow-md rounded-md">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b">Sr. No.</th>
              <th className="py-2 px-4 border-b">Bus Number</th>
              <th className="py-2 px-4 border-b">Driver</th>
              <th className="py-2 px-4 border-b">Driver Mob No.</th>
              <th className="py-2 px-4 border-b">Conductor</th>
              <th className="py-2 px-4 border-b">Conductor Mob No.</th>
              <th className="py-2 px-4 border-b">Student Capacity</th>
              <th className="py-2 px-4 border-b">Actions</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {busDetails.map((bus, index) => (
              <tr key={bus._id}>
                <td className="py-2 px-4 border-b">{index + 1}</td>
                <td className="py-2 px-4 border-b">{bus.busNumber}</td>
                <td className="py-2 px-4 border-b">{bus.driver}</td>
                <td className="py-2 px-4 border-b">{bus.driverMobNumber}</td>
                <td className="py-2 px-4 border-b">{bus.conductor}</td>
                <td className="py-2 px-4 border-b">{bus.conductorMobNumber}</td>
                <td className="py-2 px-4 border-b">{bus.studentCapacity}</td>
                <td className="py-2 px-4 border-b">
                  <button
                    onClick={() => handleEdit(bus)}
                    className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600 mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(bus._id)}
                    className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ManageBus;
